import { useMemo, useState, type FormEvent } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import { useCourses, useCreateInfostaff, useDepartments, useInfostaff, useMarkInfostaffRead } from '../api/hooks';
import { useT } from '../i18n/LangProvider';
import { Query, fmtDate, Pill } from '../ui/atoms';
import { Icon } from '../ui/Icon';
import { RichTextEditor, isHtml, sanitizeHtml } from '../ui/RichTextEditor';

const BASE_CATEGORIES = ['Operativo', 'Amministrazione', 'HR', 'IT', 'Marketing', 'Sicurezza', 'Generale'];

export function InfostaffScreen({ onNav }: { onNav?: (p: string) => void }) {
  const t = useT();
  const auth = useAuth();
  const q = useInfostaff();
  const coursesQ = useCourses();
  const deptsQ = useDepartments();
  const markRead = useMarkInfostaffRead();
  const create = useCreateInfostaff();
  const canPublish = auth.hasRole(Roles.ADMIN) || auth.hasRole(Roles.TEAM_LEAD);

  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'Operativo', urgent: false, body: '', courseId: '', audience: 'all', sendEmail: true });

  const courses = coursesQ.data ?? [];
  const courseById = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);
  const departments = deptsQ.data ?? [];
  const deptById = useMemo(() => new Map(departments.map((d) => [d.id, d])), [departments]);
  const audienceLabel = (a?: string) => (a && a !== 'all' ? deptById.get(a)?.name ?? t('Reparto') : t('Tutti'));

  const categories = useMemo(() => {
    const set = new Set(BASE_CATEGORIES);
    for (const i of q.data?.items ?? []) if (i.category) set.add(i.category);
    return [...set];
  }, [q.data]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const plain = form.body.replace(/<[^>]+>/g, '').trim();
    if (!form.title.trim() || !plain) return;
    await create.mutateAsync({
      title: form.title.trim(),
      body: form.body,
      category: form.category || undefined,
      urgent: form.urgent,
      courseId: form.courseId || undefined,
      audience: form.audience,
      sendEmail: form.sendEmail,
    });
    setForm({ title: '', category: 'Operativo', urgent: false, body: '', courseId: '', audience: 'all', sendEmail: true });
    setShowNew(false);
  }

  return (
    <div style={{ padding: 24 }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="h1">Infostaff</h1>
          <div className="page-head__sub">{t('Comunicazioni interne al team.')}</div>
        </div>
        {canPublish && (
          <button className="btn btn--brand" onClick={() => setShowNew((s) => !s)}><Icon name="plus" size={14} /> {t('Nuovo infostaff')}</button>
        )}
      </div>

      {showNew && (
        <form className="card" style={{ padding: 18, marginBottom: 20 }} onSubmit={submit}>
          <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <input className="input" placeholder={t('Titolo comunicazione')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required style={{ flex: 2, minWidth: 220 }} />
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={{ flex: 1, minWidth: 160 }}>
              {categories.map((c) => <option key={c} value={c}>{t(c)}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <RichTextEditor value={form.body} onChange={(html) => setForm({ ...form, body: html })} minHeight={200} placeholder={t('Scrivi la comunicazione… (usa la barra per formattare, immagini, video e link)')} />
          </div>
          <div className="row" style={{ gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Icon name="cap" size={14} />
            <span className="muted" style={{ fontSize: 13 }}>{t('Allega un corso (facoltativo):')}</span>
            <select className="input" value={form.courseId} onChange={(e) => setForm({ ...form, courseId: e.target.value })} style={{ minWidth: 220 }}>
              <option value="">{t('— nessun corso —')}</option>
              {courses.filter((c) => c.published).map((c) => <option key={c.id} value={c.id}>{c.title}{c.mandatory ? t(' · obbligatorio') : ''}</option>)}
            </select>
          </div>
          <div className="row" style={{ gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Icon name="users" size={14} />
            <span className="muted" style={{ fontSize: 13 }}>{t('Destinatari:')}</span>
            <select className="input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} style={{ minWidth: 200 }}>
              <option value="all">{t('Tutti')}</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <label className="row" style={{ gap: 8, cursor: 'pointer', fontSize: 13, alignItems: 'center' }}>
              <button type="button" className={`checkbox ${form.sendEmail ? 'is-on' : ''}`} onClick={() => setForm({ ...form, sendEmail: !form.sendEmail })}>
                {form.sendEmail && <Icon name="check" size={11} stroke={3} />}
              </button>
              <span className="row" style={{ gap: 5, alignItems: 'center' }}><Icon name="mail" size={14} /> {t('Invia anche via email')}</span>
            </label>
          </div>
          <div className="spaced">
            <label className="row" style={{ gap: 8, cursor: 'pointer', fontSize: 13 }}>
              <button type="button" className={`checkbox ${form.urgent ? 'is-on' : ''}`} onClick={() => setForm({ ...form, urgent: !form.urgent })}>
                {form.urgent && <Icon name="check" size={11} stroke={3} />}
              </button>
              {t('Segna come urgente')}
            </label>
            <div className="cluster">
              <button type="button" className="btn btn--sm" onClick={() => setShowNew(false)}>{t('Annulla')}</button>
              <button type="submit" className="btn btn--sm btn--brand" disabled={create.isPending}>{t('Pubblica')}</button>
            </div>
          </div>
        </form>
      )}

      <Query query={q} empty={{ test: (d) => d.items.length === 0, title: t('Nessuna comunicazione'), hint: canPublish ? t('Pubblica la prima con “Nuovo infostaff”.') : t('Le comunicazioni interne pubblicate compaiono qui.') }}>
        {(d) => (
          <div className="col" style={{ gap: 12 }}>
            {d.items.map((i) => (
              <div key={i.id} className="card" style={{ padding: 16, borderLeft: i.urgent ? '3px solid var(--danger, #c0392b)' : undefined, opacity: i.read ? 0.75 : 1 }}>
                <div className="spaced" style={{ marginBottom: 6 }}>
                  <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                    {i.urgent && <Pill tone="warn">{t('Urgente')}</Pill>}
                    {i.category && <Pill>{t(i.category)}</Pill>}
                    <strong>{i.title}</strong>
                  </div>
                  <span className="muted" style={{ fontSize: 12 }}>
                    <span className="row" style={{ gap: 4, alignItems: 'center', display: 'inline-flex' }} title={`Destinatari: ${audienceLabel(i.audience)}`}>
                      <Icon name="users" size={11} /> {audienceLabel(i.audience)}
                    </span>
                    {' · '}{i.author} · {fmtDate(i.publishedAt)}
                  </span>
                </div>
                {isHtml(i.body)
                  ? <div className="md-body" style={{ fontSize: 13.5, margin: '8px 0' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(i.body) }} />
                  : <p className="muted" style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', margin: '8px 0' }}>{i.body}</p>}
                {i.courseId && courseById.get(i.courseId) && (
                  <button className="infostaff-course" onClick={() => onNav?.('learning')}>
                    <span className={`lrn-course__ico is-${courseById.get(i.courseId)!.color ?? 'orange'}`} style={{ width: 34, height: 34 }}>
                      <Icon name="cap" size={16} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ink-4)', fontWeight: 700 }}>{t('Corso allegato')}</span>
                      <span style={{ fontWeight: 600 }}>{courseById.get(i.courseId)!.title}</span>
                    </span>
                    <span className="btn btn--sm btn--brand" style={{ marginLeft: 'auto' }}><Icon name="play" size={12} /> {t('Vai al corso')}</span>
                  </button>
                )}
                {!i.read && (
                  <button className="btn btn--sm" onClick={() => markRead.mutate(i.id)}>{t('Segna come letto')}</button>
                )}
              </div>
            ))}
          </div>
        )}
      </Query>
    </div>
  );
}
